import React, { useEffect, useState, useCallback } from "react";
import {
  Card, CardBody, CardFooter, CardTitle, Row, Col, Table, Button, Nav, NavItem, NavLink, TabContent, TabPane
} from "reactstrap";
import {
  getMonthlyRevenue,
  getTotalRevenue,
  getTodayRevenue,
  recalculateAllRevenue
} from "../Payments/service/paymentsService";
import { fetchAllPayments } from "../Clients/service/paymentsService";
import { getClientsCount } from "../Clients/service/clientsService";
import { fetchAllBookings } from "../Bookings/service/bookingService";
import { useSede } from "context/SedeContext";
import { useAuth } from "context/AuthContext";
import useUserActivity from "../../../hooks/useUserActivity";
import useSedeRefresh from "../../../hooks/useSedeRefresh";
import dayjs from "dayjs";
import api from "../../../api/api";

// Import new dashboard components
import KeyMetrics from "./components/KeyMetrics";
import BookingsCharts from "./components/BookingsCharts";
import MembershipAnalysis from "./components/MembershipAnalysis";
import PaymentAnalysis from "./components/PaymentAnalysis";
import ClientAnalysis from "./components/ClientAnalysis";

const DashboardStats = () => {
  const { addSedeParams, addSedeHeaders, isGlobalMode, getSelectionText } = useSede();
  const { isAuthenticated, user } = useAuth();
  const { isUserActive } = useUserActivity();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    // Key metrics
    todayBookings: 0,
    todayRevenue: 0,
    activeClients: 0,
    newClients: 0,
    monthlyRevenue: 0,
    totalRevenue: 0,

    // Bookings data
    dailyBookings: [],
    attendanceStats: { attended: 0, no_show: 0, cancelled: 0 },
    timeSlotStats: [],

    // Membership data
    membershipStats: [],
    membershipDetails: [],
    renewalStats: [],

    // Payment data
    dailyRevenue: [],
    paymentMethods: [],
    recentPayments: [],
    monthlyRevenue: [],

    // Client data
    ageGroups: [],
    clientStatus: { active: 0, inactive: 0, new: 0, at_risk: 0 },
    topClients: [],
    clientsAtRisk: []
  });

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);

      // Verificar autenticación
      if (!isAuthenticated) {
        console.error('User not authenticated');
        return;
      }

      const token = localStorage.getItem('access');

      if (!token) {
        console.error('No authentication token found');
        return;
      }

      // Preparar parámetros y headers de sede para las consultas
      const sedeParams = addSedeParams({});
      const sedeHeaders = addSedeHeaders({});


      // Usar el nuevo endpoint optimizado
      const response = await api.get('/studio/dashboard-data/', {
        params: sedeParams,
        headers: sedeHeaders
      });


      // Los datos ya vienen pre-procesados del backend
      setDashboardData(response.data);
      setLastUpdated(new Date());

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      console.error('Error details:', error.response?.data);
      console.error('Error status:', error.response?.status);

      // Si es error de autenticación, mostrar mensaje
      if (error.response?.status === 401) {
        console.error('Authentication failed. Please login again.');
      }
    } finally {
      setLoading(false);
    }
  }, [addSedeParams, addSedeHeaders, isAuthenticated, user]);

  useEffect(() => {
    // Solo refrescar automáticamente cada 5 minutos (300000ms)
    // y solo si el usuario está activo y el auto-refresh está habilitado
    const interval = setInterval(() => {
      if (autoRefresh && isUserActive()) {
        fetchStats();
      }
    }, 300000); // 5 minutos

    // Limpiar el intervalo cuando el componente se desmonte
    return () => clearInterval(interval);
  }, [fetchStats, autoRefresh]);

  // Carga inicial
  useEffect(() => {
    fetchStats();
  }, []); // Solo se ejecuta una vez al montar

  // Escuchar cambios de sede y refrescar automáticamente
  useSedeRefresh(fetchStats, [fetchStats]);

  const handleRecalculate = async () => {
    await recalculateAllRevenue();
    fetchStats();
  };

  if (!isAuthenticated) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="text-center">
          <div className="alert alert-warning" role="alert">
            <h4 className="alert-heading">No autenticado</h4>
            <p>Por favor, inicia sesión para acceder al dashboard.</p>
            <hr />
            <p className="mb-0">Redirigiendo al login...</p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Cargando...</span>
          </div>
          <p className="mt-3">Cargando datos del dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Simple Dashboard Header */}

      
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h4 className="mb-0">Dashboard Completo</h4>
              <p className="text-muted mb-0">Vista general del negocio</p>
            </div>
            <div className="d-flex align-items-center gap-2">
              <Button
                color="primary"
                size="sm"
                onClick={handleRecalculate}
                disabled={loading}
                style={{
                  backgroundColor: "#28a745",
                  borderColor: "#28a745",
                  padding: "0.3rem 0.6rem",
                  fontSize: "0.75rem"
                }}
              >
                {loading ? '⏳' : '🔄'}
              </Button>
            </div>
          </div>

          <div className="d-flex align-items-center gap-3">
            {lastUpdated && (
              <small className="text-muted">
                Última actualización: {lastUpdated.toLocaleTimeString()}
              </small>
            )}

            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="autoRefresh"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="autoRefresh" style={{ fontSize: "0.8rem" }}>
                Auto-refresh (5min)
              </label>
            </div>
          </div>


      {/* Navigation Tabs */}
      <Card className="mb-4" style={{ boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
        <CardBody className="p-0">
          <Nav tabs className="nav-tabs-primary" style={{ borderBottom: 'none' }}>
            <NavItem>
              <NavLink
                className={activeTab === 'overview' ? 'active' : ''}
                onClick={() => setActiveTab('overview')}
                style={{
                  cursor: 'pointer',
                  border: 'none',
                  borderBottom: activeTab === 'overview' ? '3px solid #007bff' : '3px solid transparent',
                  backgroundColor: activeTab === 'overview' ? '#f8f9fa' : 'transparent',
                  color: activeTab === 'overview' ? '#007bff' : '#6c757d',
                  fontWeight: activeTab === 'overview' ? '600' : '400',
                  padding: '1rem 1.5rem'
                }}
              >
                📊 Resumen General
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={activeTab === 'bookings' ? 'active' : ''}
                onClick={() => setActiveTab('bookings')}
                style={{
                  cursor: 'pointer',
                  border: 'none',
                  borderBottom: activeTab === 'bookings' ? '3px solid #007bff' : '3px solid transparent',
                  backgroundColor: activeTab === 'bookings' ? '#f8f9fa' : 'transparent',
                  color: activeTab === 'bookings' ? '#007bff' : '#6c757d',
                  fontWeight: activeTab === 'bookings' ? '600' : '400',
                  padding: '1rem 1.5rem'
                }}
              >
                📅 Reservas
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={activeTab === 'memberships' ? 'active' : ''}
                onClick={() => setActiveTab('memberships')}
                style={{
                  cursor: 'pointer',
                  border: 'none',
                  borderBottom: activeTab === 'memberships' ? '3px solid #007bff' : '3px solid transparent',
                  backgroundColor: activeTab === 'memberships' ? '#f8f9fa' : 'transparent',
                  color: activeTab === 'memberships' ? '#007bff' : '#6c757d',
                  fontWeight: activeTab === 'memberships' ? '600' : '400',
                  padding: '1rem 1.5rem'
                }}
              >
                🎯 Membresías
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={activeTab === 'payments' ? 'active' : ''}
                onClick={() => setActiveTab('payments')}
                style={{
                  cursor: 'pointer',
                  border: 'none',
                  borderBottom: activeTab === 'payments' ? '3px solid #007bff' : '3px solid transparent',
                  backgroundColor: activeTab === 'payments' ? '#f8f9fa' : 'transparent',
                  color: activeTab === 'payments' ? '#007bff' : '#6c757d',
                  fontWeight: activeTab === 'payments' ? '600' : '400',
                  padding: '1rem 1.5rem'
                }}
              >
                💰 Pagos
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={activeTab === 'clients' ? 'active' : ''}
                onClick={() => setActiveTab('clients')}
                style={{
                  cursor: 'pointer',
                  border: 'none',
                  borderBottom: activeTab === 'clients' ? '3px solid #007bff' : '3px solid transparent',
                  backgroundColor: activeTab === 'clients' ? '#f8f9fa' : 'transparent',
                  color: activeTab === 'clients' ? '#007bff' : '#6c757d',
                  fontWeight: activeTab === 'clients' ? '600' : '400',
                  padding: '1rem 1.5rem'
                }}
              >
                👥 Clientes
              </NavLink>
            </NavItem>
          </Nav>

          <TabContent activeTab={activeTab} className="p-4">
            <TabPane tabId="overview">
              <KeyMetrics data={dashboardData} />
              <BookingsCharts data={dashboardData} />
            </TabPane>

            <TabPane tabId="bookings">
              <BookingsCharts data={dashboardData} />
            </TabPane>

            <TabPane tabId="memberships">
              <MembershipAnalysis data={dashboardData} />
            </TabPane>

            <TabPane tabId="payments">
              <PaymentAnalysis data={dashboardData} />
            </TabPane>

            <TabPane tabId="clients">
              <ClientAnalysis data={dashboardData} />
            </TabPane>
          </TabContent>
        </CardBody>
      </Card>
    </>
  );
};

export default DashboardStats;
